import { Box, Button, Chip, CircularProgress, Paper, Tab, Tabs, Table, TableBody, TableContainer, TableHead, TableRow, TextField, Typography, useTheme } from "@mui/material";
import { useConstants } from "../../hooks/UseConstants";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { usePopups } from "../../hooks/UsePopups";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import WithdrawalRequestsFilter from "../../popup/WithdrawalRequestsFilter";
import { useWaits } from "../../hooks/UseWait";
import Fetch from "../../services/Fetch";
import { useWithdrawFilter } from "../../filter/UseWithdrawFilter";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearch } from "../../hooks/UseSearch";
import { usePagination } from "../../hooks/UsePagination";
import AdminPageHero from "../../components/AdminPageHero";
import AdminFinanceNav from "../../components/AdminFinanceNav";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import useSnackBar from "../../hooks/UseSnackBar";
import SnackbarAlert from "../../components/SnackBar";
import PageContent from "../../components/PageContent";
import ModalOverlay from "../../components/ModalOverlay";
import AdminListToolbar from "../../components/AdminListToolbar";
import { formatCurrency } from "../../helper/formatCurrency";

function formatDate(value, isAr) {
    if (!value) return isAr ? "غير متوفر" : "Unavailable";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    return new Intl.DateTimeFormat(isAr ? "ar-EG" : "en-US", {
        dateStyle: "medium",
    }).format(parsed);
}

function ProfitWithdrawalRequests() {
    const intl = useIntl();
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { language, host } = useConstants();
    const { filterWait, setFilterWait, getWait, setGetWait } = useWaits();
    const { search, setSearch, order, setOrder } = useSearch();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const { from, setFrom, to, setTo, teacherId, setTeacherId, teacherValue, setTeacherValue } = useWithdrawFilter();
    const [withdrawCounts, setWithdrawCounts] = useState(0);
    const [withdraws, setWithdraws] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(true);
    const [tab, setTab] = useState(0);
    const [requestNotes, setRequestNotes] = useState({});
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const isAr = language === "ar";

    const historyUrl = useMemo(() => (
        `${host}/admin/profit/withdraws?page=${page + 1}${order || ""}${search ? `&search=${search}` : ""}${from ? `&from=${from}` : ""}${to ? `&to=${to}` : ""}${teacherId ? `&user_id=${teacherId}` : ""}`
    ), [host, page, order, search, from, to, teacherId]);

    const getMethodLabel = (method) => {
        if (!method) return isAr ? "غير محدد" : "Not specified";
        if (method === "wallet") return isAr ? "محفظة" : "Wallet";
        if (method === "cash") return isAr ? "نقداً" : "Cash";
        return isAr ? "تحويل بنكي" : "Bank transfer";
    };

    const getPendingRequests = async () => {
        setPendingLoading(true);
        const result = await Fetch(`${host}/admin/profit/withdrawal-requests?status=pending&per_page=50`, "GET");
        if (result.status === 200) {
            const { items } = parsePaginatedResponse(result);
            setPendingRequests(items);
        } else {
            setPendingRequests([]);
        }
        setPendingLoading(false);
    };

    const getWithdraws = async () => {
        const result = await Fetch(historyUrl);

        if (result.status === 200) {
            setTotalPages(result.data.pagination.last_page);
            setWithdrawCounts(result.data.pagination.total);
            setCurrentPage(page);
            setWithdraws(result.data.data);
        } else {
            setWithdraws([]);
            setWithdrawCounts(0);
            setTotalPages(1);
        }

        setGetWait(false);
    };

    const filteringWithdraw = async () => {
        const result = await Fetch(historyUrl);

        if (result.status === 200) {
            setTotalPages(result.data.pagination.last_page);
            setWithdrawCounts(result.data.pagination.total);
            setCurrentPage(page);
            setWithdraws(result.data.data);
            setPopup("filter", "none");
        }

        setFilterWait(false);
    };

    const handleRequestAction = async (id, action) => {
        const formData = new FormData();
        const notes = requestNotes[id]?.trim();
        if (notes) formData.append("admin_notes", notes);

        const result = await Fetch(`${host}/admin/profit/withdrawal-requests/${id}/${action}`, "POST", formData);
        if (result.status === 200) {
            setSnackBar("success", isAr ? "تمت المعالجة" : "Processed");
            setRequestNotes((prev) => ({ ...prev, [id]: "" }));
            getPendingRequests();
            if (tab === 1) getWithdraws();
        } else {
            setSnackBar("error", result.data?.errors?.[0] || result.data?.message || "Error");
        }
    };

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getWithdraws();
            getPendingRequests();
        }
    }, [wait, search, order, page]);

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <AdminPageHero />
                <AdminFinanceNav />
                <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
                    <Tab label={isAr ? `طلبات معلقة (${pendingRequests.length})` : `Pending (${pendingRequests.length})`} />
                    <Tab label={isAr ? "سجل السحب" : "Withdrawal history"} />
                </Tabs>

                {tab === 0 && (
                    <DashboardPanelCard
                        title={isAr ? "طلبات سحب الأرباح المعلقة" : "Pending withdrawal requests"}
                        subtitle={isAr ? "راجع طلبات السحب الجديدة، وأضف ملاحظات داخلية قبل الموافقة أو الرفض." : "Review new withdrawal requests and add internal notes before approval or rejection."}
                        className="mx-0 !mt-0"
                    >
                        {pendingLoading ? (
                            <Box className="flex justify-center py-12"><CircularProgress /></Box>
                        ) : pendingRequests.length === 0 ? (
                            <Typography color="text.secondary" className="text-center py-12">
                                {isAr ? "لا توجد طلبات معلقة" : "No pending requests"}
                            </Typography>
                        ) : (
                            <TableContainer component={Paper} sx={{ boxShadow: "none", borderRadius: 3 }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc" }}>
                                            <StyledTableCell>{isAr ? "المدرس" : "Teacher"}</StyledTableCell>
                                            <StyledTableCell>{isAr ? "المبلغ" : "Amount"}</StyledTableCell>
                                            <StyledTableCell>{isAr ? "الاستلام" : "Payout"}</StyledTableCell>
                                            <StyledTableCell>{isAr ? "التاريخ" : "Date"}</StyledTableCell>
                                            <StyledTableCell>{isAr ? "ملاحظات الأدمن" : "Admin notes"}</StyledTableCell>
                                            <StyledTableCell>{isAr ? "إجراء" : "Action"}</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pendingRequests.map((req) => (
                                            <StyledTableRow key={req.id}>
                                                <StyledTableCell>
                                                    {req.teacher?.first_name} {req.teacher?.last_name}
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ fontWeight: 700, color: "#0D358C" }}>
                                                    {formatCurrency(req.amount, isAr)}
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Chip size="small" variant="outlined" label={getMethodLabel(req.payout_method)} sx={{ mb: req.payout_details ? 0.75 : 0 }} />
                                                    {req.payout_details ? (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {req.payout_details}
                                                        </Typography>
                                                    ) : null}
                                                </StyledTableCell>
                                                <StyledTableCell>{formatDate(req.created_at, isAr)}</StyledTableCell>
                                                <StyledTableCell>
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        placeholder={isAr ? "ملاحظة داخلية أو سبب الرفض" : "Internal note or rejection reason"}
                                                        value={requestNotes[req.id] ?? ""}
                                                        onChange={(e) => setRequestNotes((prev) => ({ ...prev, [req.id]: e.target.value }))}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                        <Button size="small" color="success" variant="contained" onClick={() => handleRequestAction(req.id, "approve")}>
                                                            {isAr ? "موافقة" : "Approve"}
                                                        </Button>
                                                        <Button size="small" color="error" variant="contained" onClick={() => handleRequestAction(req.id, "reject")}>
                                                            {isAr ? "رفض" : "Reject"}
                                                        </Button>
                                                    </Box>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </DashboardPanelCard>
                )}

                {tab === 1 && (
                    <DashboardPanelCard
                        title={isAr ? "سجل سحوبات الأرباح" : "Withdrawal history"}
                        subtitle={isAr ? "استعرض السجل المالي السابق مع طريقة السحب والملاحظات المرتبطة بكل عملية." : "Review past withdrawal records along with payout method and admin notes."}
                        className="mx-0 !mt-0"
                    >
                        <AdminListToolbar
                            isAr={isAr}
                            searchPlaceholder={intl.formatMessage({ id: "search_earnings_record" })}
                            search={search}
                            onSearchChange={setSearch}
                            order={order}
                            onOrderChange={setOrder}
                            totalLabel={intl.formatMessage({ id: "total_record" })}
                            totalCount={withdrawCounts}
                            onFilterClick={() => setPopup("filter", "flex")}
                            orderOptions={[
                                { value: "", label: intl.formatMessage({ id: "date" }) },
                                { value: "&order_by=amount&direction=asc", label: intl.formatMessage({ id: "amount" }) },
                            ]}
                        />

                        <TableContainer component={Paper} dir={language === "en" ? "ltr" : "rtl"} sx={{ boxShadow: "none", borderRadius: 3 }}>
                            <Table sx={{ minWidth: 700 }} aria-label="withdrawal history table">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8fafc" }}>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="record_id" /></StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="teacher_name" /></StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>{isAr ? "طريقة السحب" : "Payout method"}</StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="available_balance" /></StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}>{isAr ? "ملاحظات الأدمن" : "Admin notes"}</StyledTableCell>
                                        <StyledTableCell align={language === "en" ? "left" : "right"}><FormattedMessage id="date" /></StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {withdraws.length > 0 ? withdraws.map((withdraw) => (
                                        <StyledTableRow key={withdraw.id}>
                                            <StyledTableCell align={language === "en" ? "left" : "right"} component="th" scope="row">
                                                {withdraw.id}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                <Box className="flex flex-row-reverse items-center justify-end">
                                                    <Box className="mr-2">{withdraw.user.first_name} {withdraw.user.last_name}</Box>
                                                    <Box className="w-7 h-7 rounded-full bg-gray-300 flex justify-center items-center font-bold">
                                                        {withdraw.user.first_name.charAt(0)}{withdraw.user.last_name.charAt(0)}
                                                    </Box>
                                                </Box>
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                <Chip size="small" variant="outlined" label={getMethodLabel(withdraw.payable?.payout_method)} />
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"} sx={{ fontWeight: 700, color: "#0D358C" }}>
                                                {formatCurrency(withdraw.amount, isAr)}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                {withdraw.payable?.admin_notes || "-"}
                                            </StyledTableCell>
                                            <StyledTableCell align={language === "en" ? "left" : "right"}>
                                                {formatDate(withdraw.created_at, isAr)}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )) : (
                                        <StyledTableRow>
                                            <StyledTableCell colSpan={6} align="center">
                                                <Typography color="text.secondary" sx={{ py: 4 }}>
                                                    {isAr ? "لا يوجد سجل سحب مطابق حالياً." : "No matching withdrawal history found."}
                                                </Typography>
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box className="flex justify-center items-center" dir="rtl" sx={{ mt: 2 }}>
                            <Button disabled={page + 1 === totalPages || totalPages === 0} onClick={() => setPage(currentPage + 1)}>
                                <NavigateNextIcon fontSize="large" />
                            </Button>
                            <Typography variant="body1" className="!text-xl" dir="ltr">{totalPages ? currentPage + 1 : 0} / {totalPages || 0}</Typography>
                            <Button disabled={page + 1 === 1} onClick={() => setPage(currentPage - 1)}>
                                <NavigateBeforeIcon fontSize="large" />
                            </Button>
                        </Box>
                    </DashboardPanelCard>
                )}
            </PageContent>

            <ModalOverlay id="filter" onClose={() => setPopup("filter", "none")}>
                <WithdrawalRequestsFilter onClickClose={() => setPopup("filter", "none")} onClickConfirm={filteringWithdraw} filterWait={filterWait} setFilterWait={setFilterWait} from={from} to={to} teachrrId={teacherId} teacherValue={teacherValue} setFrom={setFrom} setTo={setTo} setTeacherId={setTeacherId} setTeacherValue={setTeacherValue} />
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default ProfitWithdrawalRequests;
